import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {first, switchMap} from "rxjs/operators";
import {UserSubscription, UserSubscriptionType} from "../../../../../../user/user-subscription";
import {CountPipe} from "../../../../../pipes/count.pipe";
import {SizePipe} from "../../../../../pipes/size.pipe";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../../store";
import {GoogleAnalyticsService} from "../../../../../services/google-analytics/google-analytics.service";
import {Subject} from "rxjs";
import {Plan} from "../../models/one-month-free-plan";
import {filter} from "rxjs";
import {CookieService} from "ngx-cookie";
import {User} from "../../../../../../user/user";
import {ChosenPlan} from "../../../../payment-info/models/plan.model";

const PERCENT_OFFER_END_DATE =  new Date("2022-01-30 18:00:00").getTime();

@Component({
    selector: 'db-one-month-free-select-plan',
    templateUrl: './one-month-free-select-plan.component.html',
    styleUrls: ['./one-month-free-select-plan.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OneMonthFreeSelectPlanComponent implements OnInit, OnDestroy {

  @Input() isAgency: boolean = false;
  @Input() user: User;
  @Output() onPlanChange: EventEmitter<ChosenPlan> = new EventEmitter<ChosenPlan>();

  public chosenPlanIndex: number = 0;
  public chosenPlanIndexCondition: boolean;
  public plans: Plan[];
  public userSubscription: UserSubscription;
  public chosenPlanOption: string = 'year';
  public isPercentOffer = false;
  public isNewPrice = false;

  private readonly _destroyStream$ = new Subject<boolean>();

  constructor(
    private count: CountPipe,
    private size: SizePipe,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _cookieService: CookieService,
  ) { }

  ngOnInit() {
      this._getUserSubscription();
    /*this.chosenPlanIndex = this.isAgency ? 1 : 0;*/
    this.chosenPlanIndexCondition = !!this.chosenPlanIndex;
    this.isPercentOffer = (PERCENT_OFFER_END_DATE - Date.now()) > 1000;
    this.getAvailableSubscriptions();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }


  private getAvailableSubscriptions(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        first(),
        switchMap(() => this._store.select(fromStore.getUserAvailableHostingSubscriptions())),
        first(res => !!res.length),
      )
      .subscribe(availableSubscriptions => {
        this.formSubscriptions(availableSubscriptions);
        this.changePlan();
      });
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserSubscription)),
        first()
      )
      .subscribe((userSubscription: UserSubscription) => {
        this.userSubscription = userSubscription;
      });
  }

  private formSubscriptions(availableSubscriptions): void {
    const categories: string[] = ["personal", "premium", "agency"];
    const subscriptions = this.isNewPrice ? availableSubscriptions.filter(sbc => sbc && sbc.isGeo) : availableSubscriptions.filter(sbc => sbc && !sbc.isGeo);
    this.plans = subscriptions.reduce((plans, sbc) => {
      if ((sbc.type !== UserSubscriptionType.PLATFORM) || (sbc.type !== UserSubscriptionType.DEDICATEDVM) || (sbc.type === UserSubscriptionType.BOOSTER && sbc.plan && !categories.includes(sbc.plan.title.toLowerCase()))) {
        return plans;
      }
      const plan = plans.find(_plan => _plan.title.toLowerCase() === sbc.plan.title.toLowerCase());
      if (plan) {
        this.updatePlanByPeriod(plan, sbc);
      } else {
        plans = [...plans, this.createPlan(sbc)];
      }
      return plans;
    }, []);
  }

  private createPlan(sbc: UserSubscription): Plan {
    const countPipe = new CountPipe();
    const sizePipe = new SizePipe();
    const plan = new Plan(
      sbc.plan && sbc.plan.title,
      sbc.plan && sbc.plan.services && sbc.plan.services.hosting && sbc.plan.services.hosting.domain_count && sbc.plan.services.hosting.domain_count.parameter_value,
      sizePipe.transform(Number(sbc.plan.services.hosting.hosting_storage.parameter_value * 1024 * 1024)),
      countPipe.transform(parseInt(sbc.plan.services.hosting.hosting_visits.parameter_value, 10))
    );
    return this.updatePlanByPeriod(plan, sbc);
  }

  private updatePlanByPeriod(plan: Plan, sbc: UserSubscription): Plan {
    if (sbc.plan.periodNumber === 1) {
      plan.monthlyId = sbc.id;
      plan.monthlyPrice = sbc.plan.price;
    } else {
      plan.yearlyId = sbc.id;
      plan.yearlyPrice = sbc.plan.price;
    }
    return plan;
  }

  public changePlan() {

    const analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: 'Change Subscription',
      eventLabel: this.plans[this.chosenPlanIndex].title
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    const chosenPlan: ChosenPlan = this.chosenPlanOption === 'month' ? {
      id: this.plans[this.chosenPlanIndex].monthlyId,
      title: this.plans[this.chosenPlanIndex].title,
      price: this.plans[this.chosenPlanIndex].monthlyPrice,
      type: 'month'
    } : {
      id: this.plans[this.chosenPlanIndex].yearlyId,
      title: this.plans[this.chosenPlanIndex].title,
      price: this.plans[this.chosenPlanIndex].yearlyPrice,
      type: 'year'
    };
    this.onPlanChange.emit(chosenPlan);
  }

}
